import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Product } from "src/api/client-api/products/useGetOneProduct";
import { ProductScoresContext } from "src/context/Product/Scores/context";
import {
    type ProductLivePreview,
    type IngredientEcsBreakdown,
    type ProductReferenceScoresMap,
    useProductLivePreview,
} from "src/api/client-api/products/useProductLivePreview";
import { MainScore, OlapScoresResult } from "src/api/client-api/olapScores/olapScoresSchema";
import { ProductFormProductTransformations } from "src/pages/Product/ProductForm/ProductForm.types";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { formatNumber } from "src/utils/formatting";
import { WizardStepKey } from "../useWizardStepsV2";
import {
    getConsumptionSectionScoreValue,
    getDistributionSectionScoreValue,
    resolveMainScoreFromOlap,
    getIngredientLineScoreKey,
    getIngredientLineScoreValue,
    getIngredientTransportLineScoreKey,
    getPackagingLineScoreKey,
    getPackagingLineScoreValue,
    getPackagingTransportLineScoreKey,
    getPreviewSectionScoreKey,
    getRecipeSectionScoreValue,
    getSectionScoreValue,
    getTransformationLineScoreKey,
    getTransformationLineScoreValue,
    getTransformationSectionScoreValue,
    getTransportLineScoreValue,
    getProductStageTransportLineScoreKey,
    getTransportSectionScoreValue,
    PRODUCT_LEVEL_TRANSPORT_STAGES,
    resolveProductStageTransportLineValue,
} from "../sectionScores";
import { getProductIngredientTransportKey } from "../Steps/productIngredientTransportKey";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useProductFormPreviewEnabled, useProductFormScoreDirty } from "./useProductFormScoreDirty";
import { computeEcsTransportScorePerEdibleKg, ECS_SCORE_ID } from "../ecobalyseMass";

export type ProductFormScoresMode = "persisted" | "preview";

export const resolveProductFormScoresMode = (options: {
    hasExistingProduct: boolean;
    isDirty: boolean;
    hasPreviewData: boolean;
}): ProductFormScoresMode => {
    return options.hasPreviewData || !options.hasExistingProduct || options.isDirty ? "preview" : "persisted";
};

export type ProductFormScoresViewModel = {
    mode: ProductFormScoresMode;
    selectedUnit: string;
    olapScores: OlapScoresResult;
    productReferenceScores: ProductReferenceScoresMap;
    resolvedTransports: ProductLivePreview["resolvedTransports"] | undefined;
    isLoading: boolean;
    scoreQueryError: unknown | null;
    retryScoreQuery: () => Promise<unknown>;
    usePreviewSidebar: boolean;
    getDisplayedSectionScore: (sectionKey: WizardStepKey) => string | null;
    ingredientLineScores: Record<string, string | null>;
    ingredientEcsBreakdown: Record<string, IngredientEcsBreakdown>;
    packagingLineScores: Record<string, string | null>;
    transformationLineScores: Record<string, string | null>;
    transportLineScores: Record<string, string | null>;
};

type Options = {
    previewDebounceMs?: number;
    /** Campaign OLAP from `useGetProductScores` — GET /products/:id often omits `olapScores`. */
    persistedScores?: OlapScoresResult;
};

const normalizeProductReferenceScores = (value: unknown): ProductReferenceScoresMap => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(value).filter((entry): entry is [string, number | null] => {
            const score = entry[1];

            return score === null || (typeof score === "number" && Number.isFinite(score));
        }),
    );
};

export const resolveTransformationLineScore = (options: {
    mode: ProductFormScoresMode;
    lineKey: string;
    transformation: ProductFormProductTransformations;
    activeMainScore: MainScore | undefined;
    previewLineValue: number | null;
}): number | null => {
    const olapValue = getTransformationLineScoreValue(options.transformation, options.activeMainScore);
    const previewValue = options.mode === "preview" ? options.previewLineValue : null;

    return previewValue ?? olapValue ?? null;
};

export const getSelectedPreviewScoreValue = (
    scores: Record<string, number | string | null> | undefined,
    selectedScoreId: number,
): number | null => {
    if (scores === undefined) {
        return null;
    }

    const previewValue = scores[selectedScoreId.toString()] ?? null;

    if (typeof previewValue === "number") {
        return previewValue;
    }

    if (typeof previewValue === "string" && previewValue !== "") {
        const parsed = Number(previewValue);

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

export const resolveSectionScoreValue = (options: {
    sectionKey: WizardStepKey;
    mode: ProductFormScoresMode;
    product: ProductFormType;
    activeMainScore: MainScore | undefined;
    previewSectionValue: number | null;
}): number | null => {
    const { sectionKey, mode, product, activeMainScore, previewSectionValue } = options;

    let olapValue: number | null;
    switch (sectionKey) {
        case "recipe":
            olapValue = getRecipeSectionScoreValue(product.productIngredients, activeMainScore);
            break;
        case "transformation":
            olapValue = getTransformationSectionScoreValue(product.productTransformations, activeMainScore, {});
            break;
        case "distribution":
            olapValue = getDistributionSectionScoreValue(
                product.distribution?.distributionReferenceId ?? product.distributionReferenceId,
                activeMainScore,
            );
            break;
        case "consumption":
            olapValue = getConsumptionSectionScoreValue(product.productConsumptions, activeMainScore);
            break;
        default:
            olapValue = getSectionScoreValue(sectionKey, activeMainScore);
    }

    if (mode === "preview") {
        return previewSectionValue ?? olapValue;
    }

    return olapValue;
};

export const normalizePreviewTransportLineScore = (
    absoluteValue: number | null,
    totalEdibleMassG: number | undefined,
    scoreId: number,
): number | null => {
    if (absoluteValue === null) {
        return null;
    }

    if (scoreId === ECS_SCORE_ID && totalEdibleMassG !== undefined && totalEdibleMassG > 0) {
        return computeEcsTransportScorePerEdibleKg(absoluteValue, totalEdibleMassG);
    }

    return absoluteValue;
};

export const resolvePreviewLineScore = (options: {
    previewLineValue: number | null;
    olapLineValue: number | null;
}): number | null => {
    return options.previewLineValue ?? options.olapLineValue;
};

/**
 * Resolves section and line scores for the product form. Must run inside
 * `EntityScoresProvider` so line badges use the same score as the live sidebar.
 * When editing an existing product, persisted OLAP is used until calculate-preview
 * returns; the preview response then becomes authoritative for the whole form.
 */
export const useProductFormScores = (
    product: ProductFormType,
    existingProduct: Product | undefined,
    options: Options = {},
): ProductFormScoresViewModel => {
    const { previewDebounceMs = 250, persistedScores } = options;
    const { i18n } = useTranslation();
    const { lastSelectedVariation, selectedScorePrecision } = useEntityScores();
    const selectedScoreId = lastSelectedVariation.scoreId;
    const selectedUnit = lastSelectedVariation.unit;
    const isDirty = useProductFormScoreDirty(product, existingProduct);
    const previewEnabled = useProductFormPreviewEnabled(product);
    const productScoresContext = useContext(ProductScoresContext);

    const livePreview = useProductLivePreview(product, {
        enabled: previewEnabled,
        debounceMs: previewDebounceMs,
        keepPreviousData: true,
    });

    const dimensionalPersistedScores = productScoresContext?.scores?.olapScores;
    const persistedOlapScores = dimensionalPersistedScores ?? persistedScores ?? existingProduct?.olapScores ?? {};
    const previewOlapScores = livePreview.data?.olapScores ?? {};
    const mode = resolveProductFormScoresMode({
        hasExistingProduct: existingProduct !== undefined,
        isDirty,
        hasPreviewData: livePreview.data !== undefined,
    });
    const isPreviewStale =
        mode === "preview" && (livePreview.isInputPending || (livePreview.isFetching && livePreview.isPreviousData));
    const olapScores =
        mode === "persisted" ? persistedOlapScores
        : isPreviewStale ? {}
        : previewOlapScores;
    const persistedScoreQueryError = productScoresContext?.scoreQueryError ?? null;
    const arePersistedScoresRefetching = productScoresContext?.areScoresRefetching ?? false;
    const retryPersistedScores = productScoresContext?.retryScoreQuery ?? livePreview.refetch;

    const activeMainScore = useMemo(() => {
        if (mode === "preview") {
            return olapScores[selectedScoreId.toString()];
        }

        return resolveMainScoreFromOlap(olapScores, selectedScoreId);
    }, [mode, olapScores, selectedScoreId]);

    const previewTotalEdibleMassG = isPreviewStale ? undefined : livePreview.data?.ecobalyseMasses?.totalEdibleMassG;

    const formatScoreValue = (value: number | null): string | null => {
        if (value === null) {
            return null;
        }

        return formatNumber(value, i18n.resolvedLanguage, {
            maximumFractionDigits: selectedScorePrecision,
            minimumFractionDigits: selectedScorePrecision,
        });
    };

    const getPreviewLineScoreValue = (
        component: keyof ProductLivePreview["lineScores"],
        lineKey: string | null,
    ): number | null => {
        if (lineKey === null || livePreview.data === undefined) {
            return null;
        }

        return getSelectedPreviewScoreValue(livePreview.data.lineScores[component]?.[lineKey], selectedScoreId);
    };

    const getPreviewSectionScoreValue = (sectionKey: WizardStepKey): number | null => {
        if (mode !== "preview") {
            return null;
        }

        return getSelectedPreviewScoreValue(
            livePreview.data?.sectionScores[getPreviewSectionScoreKey(sectionKey)],
            selectedScoreId,
        );
    };

    return useMemo((): ProductFormScoresViewModel => {
        const persistedReferenceScores = normalizeProductReferenceScores(product.jsonAttributes?.referenceScores);
        const selectedReferenceIsPersisted =
            typeof existingProduct?.productReferenceId === "number" &&
            existingProduct.productReferenceId === product.productReference?.id;
        const productReferenceScores =
            mode === "persisted" ? persistedReferenceScores
            : isPreviewStale ? {}
            : (livePreview.data?.productReferenceScores ??
                (selectedReferenceIsPersisted ? persistedReferenceScores : {}));

        const hasLivePreview = livePreview.data !== undefined && !isPreviewStale;

        const resolveTransportLineNumeric = (lineKey: string | null): number | null => {
            if (lineKey === null) {
                return null;
            }

            const previewLineValue = getPreviewLineScoreValue("transport", lineKey);
            const rawValue =
                hasLivePreview ?
                    resolvePreviewLineScore({
                        previewLineValue,
                        olapLineValue: getTransportLineScoreValue(lineKey, activeMainScore),
                    })
                :   getTransportLineScoreValue(lineKey, activeMainScore);

            if (rawValue === null) {
                return null;
            }

            const usesPreviewAbsoluteScore = previewLineValue !== null;

            return usesPreviewAbsoluteScore || mode === "preview" ?
                    normalizePreviewTransportLineScore(rawValue, previewTotalEdibleMassG, selectedScoreId)
                :   rawValue;
        };

        const getDisplayedSectionScore = (sectionKey: WizardStepKey): string | null => {
            if (isPreviewStale) {
                return null;
            }

            if (sectionKey === "transport") {
                const legSum = getTransportSectionScoreValue(product, resolveTransportLineNumeric, activeMainScore);
                const previewSection = getPreviewSectionScoreValue("transport");

                return formatScoreValue(mode === "preview" && previewSection !== null ? previewSection : legSum);
            }

            return formatScoreValue(
                resolveSectionScoreValue({
                    sectionKey,
                    mode,
                    product,
                    activeMainScore,
                    previewSectionValue: getPreviewSectionScoreValue(sectionKey),
                }),
            );
        };

        const ingredientLineScores =
            isPreviewStale ?
                {}
            :   Object.fromEntries(
                    product.productIngredients.flatMap((ingredient) => {
                        const key = getIngredientLineScoreKey(ingredient);
                        if (key === null) {
                            return [];
                        }

                        const value =
                            mode === "preview" ?
                                resolvePreviewLineScore({
                                    previewLineValue: getPreviewLineScoreValue("ingredients", key),
                                    olapLineValue: getIngredientLineScoreValue(ingredient, activeMainScore),
                                })
                            :   getIngredientLineScoreValue(ingredient, activeMainScore);

                        return [[key, formatScoreValue(value)]];
                    }),
                );

        const packagingLineScores =
            isPreviewStale ?
                {}
            :   Object.fromEntries(
                    product.productPackagings.flatMap((packaging) => {
                        const key = getPackagingLineScoreKey(packaging);
                        if (key === null) {
                            return [];
                        }

                        const value =
                            mode === "preview" ?
                                resolvePreviewLineScore({
                                    previewLineValue: getPreviewLineScoreValue("packagings", key),
                                    olapLineValue: getPackagingLineScoreValue(packaging, activeMainScore),
                                })
                            :   getPackagingLineScoreValue(packaging, activeMainScore);

                        return [[key, formatScoreValue(value)]];
                    }),
                );

        const transformationLineScores =
            isPreviewStale ?
                {}
            :   Object.fromEntries(
                    product.productTransformations.flatMap((transformation) => {
                        const key = getTransformationLineScoreKey(transformation);
                        if (key === null) {
                            return [];
                        }

                        const value = resolveTransformationLineScore({
                            mode,
                            lineKey: key,
                            transformation,
                            activeMainScore,
                            previewLineValue: getPreviewLineScoreValue("transformations", key),
                        });

                        return [[key, formatScoreValue(value)]];
                    }),
                );

        const transportLineScores: Record<string, string | null> = {};
        if (!isPreviewStale) {
            for (const ingredient of product.productIngredients) {
                const persistedKey = getIngredientTransportLineScoreKey(ingredient.id);
                const previewKey = getProductIngredientTransportKey(ingredient);
                const value = resolveTransportLineNumeric(persistedKey) ?? resolveTransportLineNumeric(previewKey);

                if (previewKey !== null) {
                    transportLineScores[previewKey] = formatScoreValue(value);
                }
                if (persistedKey !== null) {
                    transportLineScores[persistedKey] = formatScoreValue(value);
                }
            }
            for (const packaging of product.productPackagings) {
                const key = getPackagingTransportLineScoreKey(packaging.id);
                if (key === null) {
                    continue;
                }

                transportLineScores[key] = formatScoreValue(resolveTransportLineNumeric(key));
            }

            for (const stage of PRODUCT_LEVEL_TRANSPORT_STAGES) {
                const stageKey = getProductStageTransportLineScoreKey(stage);
                transportLineScores[stageKey] = formatScoreValue(
                    resolveProductStageTransportLineValue(stage, (lineKey) => resolveTransportLineNumeric(lineKey)),
                );
            }
        }

        return {
            mode,
            selectedUnit,
            olapScores,
            productReferenceScores,
            resolvedTransports:
                mode === "preview" && !isPreviewStale ? livePreview.data?.resolvedTransports : undefined,
            isLoading:
                mode === "preview" ?
                    livePreview.isFetching || livePreview.isInputPending
                :   arePersistedScoresRefetching,
            scoreQueryError:
                mode === "preview" ?
                    isPreviewStale ? null
                    :   livePreview.error
                :   persistedScoreQueryError,
            retryScoreQuery: mode === "preview" ? livePreview.refetch : retryPersistedScores,
            usePreviewSidebar: mode === "preview",
            getDisplayedSectionScore,
            ingredientLineScores,
            ingredientEcsBreakdown: isPreviewStale ? {} : (livePreview.data?.ingredientEcsBreakdown ?? {}),
            packagingLineScores,
            transformationLineScores,
            transportLineScores,
        };
    }, [
        activeMainScore,
        arePersistedScoresRefetching,
        previewTotalEdibleMassG,
        formatScoreValue,
        isPreviewStale,
        livePreview.data,
        livePreview.error,
        livePreview.isFetching,
        livePreview.isInputPending,
        livePreview.refetch,
        mode,
        olapScores,
        persistedScoreQueryError,
        product,
        retryPersistedScores,
        selectedScoreId,
        selectedUnit,
        existingProduct?.productReferenceId,
    ]);
};
